import { Role } from '../types/auth';

/**
 * Roles cuyos usuarios pueden aparecer como "Creador" en los dropdowns de
 * filtro. Incluye RoomTeam aunque sea principalmente lectura — puede haber
 * históricos donde figure como creador de actuaciones/accidentes/comms y
 * el usuario quiere poder filtrarlos.
 *
 * EXCLUYE: operarios externos sin acceso, empresas y tipos virtuales.
 */
const MULTIMEDIA_ALLOWED_ROLES: ReadonlySet<string> = new Set([
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.RoomTeam,
  Role.Tablet,
]);

export type MultimediaUserOption = { label: string; value: string };

type FilterableUser = {
  id: string;
  name?: string;
  surName?: string;
  isActive?: boolean;
  roles?: string[];
  userType?: string;
};

/**
 * Devuelve los usuarios con rol {SuperAdmin, Coex, Tablet} ordenados
 * alfabéticamente por "Nombre Apellido" (es, sensitivity: base).
 *
 * Por defecto excluye `isActive=false` (caso operativo 95%). Si
 * `includeInactive=true`, los incluye con sufijo "(inactivo)" en el label
 * para auditoría / informes históricos.
 *
 * Reusa la lista cacheada por el loader (`FETCH_USERS` ya pide `roles` e
 * `isActive`). Cero cambios en GraphQL.
 */
export function filterAndSortMultimediaUsers(
  users: FilterableUser[] | undefined | null,
  includeInactive = false,
): MultimediaUserOption[] {
  if (!users) return [];
  return users
    .filter(
      (u) =>
        (includeInactive || u.isActive === true) &&
        Array.isArray(u.roles) &&
        u.roles.some((r) => MULTIMEDIA_ALLOWED_ROLES.has(r)),
    )
    .map((u) => ({
      label:
        u.isActive === false
          ? `${u.name ?? ''} ${u.surName ?? ''} (inactivo)`.trim()
          : `${u.name ?? ''} ${u.surName ?? ''}`.trim(),
      value: u.id,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
    );
}

/**
 * Devuelve los usuarios tipo "operator" ordenados alfabéticamente por
 * "Nombre Apellido" (es, sensitivity: base). Se usa para el dropdown
 * "Operarios" en las tablas de Actuaciones.
 *
 * Por defecto excluye `isActive=false`. Si `includeInactive=true`, los
 * incluye con sufijo "(inactivo)" para auditoría histórica.
 *
 * Diferente de filterAndSortMultimediaUsers (que filtra por roles —
 * creadores). Aquí filtramos por userType, que identifica al operario de
 * campo sin login. Ver context/ARCHITECTURE.md §6 (Tipo C exclusives) y
 * memoria project_team_vs_user_on_orders.
 */
export function filterAndSortOperators(
  users: FilterableUser[] | undefined | null,
  includeInactive = false,
): MultimediaUserOption[] {
  if (!users) return [];
  return users
    .filter(
      (u) =>
        (includeInactive || u.isActive === true) && u.userType === 'operator',
    )
    .map((u) => ({
      label:
        u.isActive === false
          ? `${u.name ?? ''} ${u.surName ?? ''} (inactivo)`.trim()
          : `${u.name ?? ''} ${u.surName ?? ''}`.trim(),
      value: u.id,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
    );
}
